/* eslint-disable max-len */
import React, {useEffect, useState} from 'react';
import AgGridTable from '@components/system/AgGridTable/AgGridTable';
import {axios, CancelTokenSource} from '@shared/axios';
import {getAllClaims} from '@shared/services/ClaimsServices';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import Loader from '@components/framework/loader/Loader';
import {useSelector} from 'react-redux';
// import { textFieldStyles } from "../../Invoices/TableDataAndStyles";
import {
  FilterClinicStyles,
  getStatusLabel
} from '../ClaimsAnalysis/ClaimsAnalysis';
import {useTranslation} from 'react-i18next';
import HelperUtils from '@shared/utils/HelperUtils';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {Link} from '@material-ui/core';
import {
  CloseAndAdjustBalanceToPatientClaim,
  MarkForSubmission,
  ReverseClaim
} from '@shared/services/VaccinesServicesClaims';
import {SendClaim} from '@shared/services/ClaimsServices';
import {Permission} from '@shared/services/PermissionService';
import {
  AppPermissions,
  SendClaimEligibleStatuses,
  PharmacyClaimStatus
} from '@shared/constants/Mapping';
import IgnoreDialog from '../IgnoreDialog/IgnoreDialog';
import './../PharmacyClaimsPatientDetailsDrawer/PharmacyClaimsPatientDetailsDrawerStyles.scss';
import {formatApiMessage} from '@shared/utils/formatApiMessage';

const PharmacyClaimsPatientDetailsDrawer = (): React.ReactElement => {
  // const classes = textFieldStyles();
  const ClinicClasses = FilterClinicStyles();
  const {t} = useTranslation();
  const [claims, setClaims] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loaderValue, setLoaderValue] = useState<any>({
    number: 0,
    name: ''
  });
  const [forceUpdate, setForceUpdate] = useState(-1);
  // const [selectedClaim, setSelectedClaim] = useState<any>({});
  const [ignoreClaimId, setIgnoreClaimId] = useState<any>({});
  const [ignoreOpenDialog, setIgnoreOpenDialog] = useState<boolean>(false);
  const [sendClaimLoadingIds, setSendClaimLoadingIds] = useState<number[]>([]);
  const {enqueueSnackbar} = useSnackbar();
  const groupId: any = useSelector((state: any) => state.auth.groupId);
  const userPermissions: any = useSelector(
    (state: any) => state.auth.userPermissions
  );
  const clinicId: any = useSelector(
    (state: any) => state.patientDetailsDrawer.clinicId
  );
  const patientDetails = useSelector(
    (state: any) => state.patientDetailsDrawer.patientDetails
  );

  const cancelSuccesiveApiCall = React.useRef<CancelTokenSource | null>(null);

  const fetchClaimsData = async (clinicGroupIdParam: number): Promise<void> => {
    try {
      setLoading(true);
      const options: any = {};
      if (patientDetails?.patient_id) {
        options.patient_id = patientDetails?.patient_id;
      }
      const cancelTokenParam: any = (cancelSuccesiveApiCall.current = axios.CancelToken.source());
      const res: any = await getAllClaims(
        clinicGroupIdParam,
        clinicId,
        options,
        cancelTokenParam
      );
      if (res) {
        setClaims(res?.data?.rows);
      } else {
        setClaims([]);
      }
      // return res;
    } catch (error: unknown) {
      enqueueSnackbar(formatApiMessage(error), SnackBarConfig('e'));
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (cancelSuccesiveApiCall.current) {
      cancelSuccesiveApiCall.current.cancel();
      // Make the cancelSuccesiveApiCall.current to null after cancelling the api call
      cancelSuccesiveApiCall.current = null;
    }
    fetchClaimsData(groupId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const renderLoader = () => {
    return <Loader size={16} />;
  };

  const handleReSubmission = async (submittingData: any) => {
    try {
      setLoaderValue((p: any) => ({
        ...p,
        number: submittingData.id,
        name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.MARK_FOR_RE_SUBMISSION')
      }));
      const res = await MarkForSubmission(
        groupId,
        clinicId,
        submittingData?.id
      );
      if (res) {
        setForceUpdate(forceUpdate + 1);
        enqueueSnackbar(
          formatApiMessage(res?.message ?? res),
          SnackBarConfig('s')
        );
      }
    } catch (err: unknown) {
      enqueueSnackbar(formatApiMessage(err), SnackBarConfig('e'));
    } finally {
      setLoaderValue((P: any) => ({
        ...P,
        number: 0,
        name: ''
      }));
    }
  };

  const handleSendClaim = async (claimData: any) => {
    try {
      setSendClaimLoadingIds(prev => [...prev, claimData.id]);
      const res = await SendClaim(groupId, clinicId, claimData?.id);
      if (res) {
        setForceUpdate(forceUpdate + 1);
        enqueueSnackbar(
          formatApiMessage(res?.message ?? res),
          SnackBarConfig('s')
        );
      }
    } catch (err: unknown) {
      enqueueSnackbar(formatApiMessage(err), SnackBarConfig('e'));
    } finally {
      setSendClaimLoadingIds(prev => prev.filter(id => id !== claimData.id));
    }
  };

  const handleCloseClaim = async (CloseClaimData: any, type: string) => {
    try {
      const params: any = {};
      if (type) {
        params.type = type;
      }
      const res = await CloseAndAdjustBalanceToPatientClaim(
        groupId,
        clinicId,
        CloseClaimData?.id,
        params
      );
      if (res) {
        setForceUpdate(forceUpdate + 1);
        enqueueSnackbar(
          formatApiMessage(res?.message ?? res),
          SnackBarConfig('s')
        );
      }
    } catch (err: unknown) {
      enqueueSnackbar(formatApiMessage(err), SnackBarConfig('e'));
    } finally {
      setLoaderValue((P: any) => ({
        ...P,
        number: 0,
        name: ''
      }));
    }
  };

  const handleReverseClaim = async (ReverseClaimData: any) => {
    try {
      const res = await ReverseClaim(groupId, clinicId, ReverseClaimData?.id);
      if (res) {
        setForceUpdate(forceUpdate + 1);
        enqueueSnackbar(
          formatApiMessage(res?.message ?? res),
          SnackBarConfig('s')
        );
      }
    } catch (err: unknown) {
      enqueueSnackbar(formatApiMessage(err), SnackBarConfig('e'));
    } finally {
      setLoaderValue((P: any) => ({
        ...P,
        number: 0,
        name: ''
      }));
    }
  };

  // // rendering the IgnoreDialog
  const renderIgnoreDialog = () => {
    return (
      <IgnoreDialog
        open={ignoreOpenDialog}
        claimId={ignoreClaimId}
        onClose={res => {
          if (res === 'confirm') {
            setIgnoreOpenDialog(false);
            setIgnoreClaimId({});
            // init(statusType);
            setLoaderValue((p: any) => ({
              ...p,
              number: 0,
              name: ''
            }));
          } else {
            setIgnoreOpenDialog(false);
            setIgnoreClaimId({});
            setLoaderValue((p: any) => ({
              ...p,
              number: 0,
              name: ''
            }));
          }
        }}
      />
    );
  };

  const claimsColumns: any = [
    {
      headerName: t('CLAIMS.CLAIM_NO'),
      field: 'prescription_reference_number',
      flex: 1,
      minWidth: 135,
      cellClass: 'left-align',
      cellRenderer: (rowData: any) => {
        return <div>{rowData.data?.prescription_reference_number || '-'}</div>;
      }
    },
    {
      headerName: t('CLAIMS.DATE_OF_SERVICE'),
      field: 'date_of_service',
      flex: 1,
      minWidth: 150,
      cellClass: 'left-align',
      cellRenderer: (rowData: any) => (
        <div>
          {HelperUtils.formatDisplayDate(rowData?.data?.date_of_service)}
        </div>
      )
    },
    {
      headerName: t('CLAIMS.CHARGES'),
      field: 'total_charges',
      flex: 1,
      minWidth: 130,
      cellClass: 'right-align',
      cellRenderer: (rowData: any) => {
        return (
          <div>
            <CurrencyFormatter
              value={rowData?.data?.total_claim_amount || '-'}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'$'}
              ignoreRound={true}
              useSign={true}
            />
          </div>
        );
      }
    },
    {
      headerName: t('CLAIMS.BALANCE'),
      field: 'balance_amount',
      flex: 1,
      minWidth: 130,
      cellClass: 'right-align',
      cellRenderer: (rowData: any) => {
        return (
          <div>
            <CurrencyFormatter
              value={rowData?.data?.balance_amount || '0'}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'$'}
              ignoreRound={true}
              useSign={true}
            />
          </div>
        );
      }
    },
    {
      headerName: t('CLAIMS.ADJUSTED_BALANCE'),
      field: 'adjusted_balance',
      flex: 1,
      minWidth: 170,
      cellClass: 'right-align',
      cellRenderer: (rowData: any) => {
        return (
          <div>
            <CurrencyFormatter
              value={rowData?.data?.adjustment_amount || '0'}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'$'}
              ignoreRound={true}
              useSign={true}
            />
          </div>
        );
      }
    },
    {
      headerName: 'Paid Amount',
      field: 'paid_amount',
      flex: 1,
      minWidth: 160,
      cellClass: 'right-align',
      cellRenderer: (rowData: any) => {
        return (
          <CurrencyFormatter
            value={rowData?.data?.total_amount_paid || '-'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      }
    },
    {
      headerName: 'Patient Balance',
      field: 'patient_balance',
      flex: 1,
      minWidth: 160,
      cellClass: 'right-align',
      cellRenderer: (rowData: any) => {
        return (
          <CurrencyFormatter
            value={rowData?.data?.patient_pay_amount || '-'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      }
    },
    {
      headerName: t('CLAIMS.STATUS'),
      field: 'status',
      flex: 1,
      minWidth: 170,
      cellClass: 'left-align',
      cellRenderer: (rowData: any) => {
        return <div>{getStatusLabel(rowData?.data?.status) || '-'}</div>;
      }
    },
    {
      headerName: t('CLAIMS.PAID_DATE'),
      field: 'paid_date',
      flex: 1,
      minWidth: 140,
      cellClass: 'left-align',
      cellRenderer: (rowData: any) => (
        <div>
          {HelperUtils.formatDisplayDate(rowData?.data?.paid_date) || '-'}
        </div>
      )
    },
    {
      headerName: t('Failure Reason'),
      field: 'failure_reason',
      flex: 1,
      minWidth: 270,
      cellClass: 'left-align',
      cellRenderer: (rowData: any) => {
        return <div>{rowData?.data?.failed_reason || '-'}</div>;
      }
    },
    {
      headerName: t('Actions'),
      field: 'action',
      flex: 1,
      minWidth: 200,
      cellClass: 'left-align',
      cellRenderer: (rowData: any) => {
        const actionLinks: React.ReactNode[] = [];
        const status = rowData?.data?.status;
        const claimId = rowData?.data?.id;

        // Rejected status actions
        if (status === PharmacyClaimStatus.REJECTED) {
          actionLinks.push(
            <Link
              key="mark-resubmission"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                handleReSubmission(rowData?.data);
              }}>
              {loaderValue.name ===
                t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.MARK_FOR_RE_SUBMISSION') &&
              loaderValue.number === claimId
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.MARK_FOR_RE_SUBMISSION')}
            </Link>
          );
          actionLinks.push(
            <Link
              key="ignore"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                setLoaderValue((p: any) => ({
                  ...p,
                  number: claimId,
                  name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.IGNORE')
                }));
                setIgnoreClaimId(claimId);
                setIgnoreOpenDialog(true);
              }}>
              {loaderValue.name === t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.IGNORE') &&
              loaderValue.number === claimId
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.IGNORE')}
            </Link>
          );
        }

        // Paid status actions
        if (status === PharmacyClaimStatus.PAID) {
          actionLinks.push(
            <Link
              key="close"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                setLoaderValue((p: any) => ({
                  ...p,
                  number: claimId,
                  name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.CLOSE_CLAIM')
                }));
                handleCloseClaim(rowData?.data, 'closed');
              }}>
              {loaderValue.name ===
                t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.CLOSE_CLAIM') &&
              loaderValue.number === claimId
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.CLOSE_CLAIM')}
            </Link>
          );
          actionLinks.push(
            <Link
              key="reverse"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                setLoaderValue((p: any) => ({
                  ...p,
                  number: claimId,
                  name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.REVERSE_CLAIM')
                }));
                handleReverseClaim(rowData?.data);
              }}>
              {loaderValue.name ===
                t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.REVERSE_CLAIM') &&
              loaderValue.number === claimId
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.REVERSE_CLAIM')}
            </Link>
          );
          actionLinks.push(
            <Link
              key="adjust-close"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                setLoaderValue((p: any) => ({
                  ...p,
                  number: claimId,
                  name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.ADJUST_CLOSE')
                }));
                handleCloseClaim(rowData?.data, 'adjusted_and_closed');
              }}>
              {loaderValue.name ===
                t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.ADJUST_CLOSE') &&
              loaderValue.number === claimId
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.ADJUST_CLOSE')}
            </Link>
          );
          actionLinks.push(
            <Link
              key="add-balance-close"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                setLoaderValue((p: any) => ({
                  ...p,
                  number: claimId,
                  name: t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.ADD_BALANCE_AND_CLOSE')
                }));
                handleCloseClaim(rowData?.data, 'added_to_patient_balance');
              }}>
              {loaderValue.name ===
                t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.ADD_BALANCE_AND_CLOSE') &&
              loaderValue.number === claimId
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.ADD_BALANCE_AND_CLOSE')}
            </Link>
          );
        }

        // Send Claim action for eligible statuses
        if (
          SendClaimEligibleStatuses.includes(status) &&
          Permission.hasPharmacyClaimPermissions(
            userPermissions,
            AppPermissions.CLAIM.SEND_CLAIM,
            groupId
          )
        ) {
          actionLinks.push(
            <Link
              key="send-claim"
              style={{padding: '0.2rem'}}
              component="button"
              onClick={() => {
                handleSendClaim(rowData?.data);
              }}>
              {sendClaimLoadingIds.includes(claimId)
                ? renderLoader()
                : t('CLAIMS.CLAIM_ANALYSIS.ACTIONS.SEND_CLAIM')}
            </Link>
          );
        }

        return (
          <div className={ClinicClasses.actions}>
            {actionLinks.length > 0 ? actionLinks : '-'}
          </div>
        );
      }
    }
  ];
  const renderPharmacyClaimTable = () => {
    return (
      <div>
        {loading ? (
          <div className="loader-styles">
            <Loader /> {/** Display loader when loading is true */}
          </div>
        ) : (
          <div
            style={{
              height: 'calc(100% - 4rem)'
            }}>
            <AgGridTable data={claims} columns={claimsColumns} />
          </div>
        )}
      </div>
    );
  };
  const renderHeader = () => {
    return (
      <div className="header">
        <p>
          <span style={{fontWeight: 600}}>
            {t('PATIENT_DETAILS_DRAWER.PHARMACY_CLAIMS_MODULE.PATIENT_NAME')}
          </span>
          {HelperUtils.formFullName(
            patientDetails?.first_name,
            patientDetails?.last_name,
            patientDetails?.middle_name
          ) || '-'}
        </p>
        <p>
          <span style={{fontWeight: 600}}>
            {t(
              'PATIENT_DETAILS_DRAWER.PHARMACY_CLAIMS_MODULE.PATIENT_ACCOUNT_NUMBER'
            )}
          </span>
          {patientDetails?.patient_account_number || '-'}
        </p>
      </div>
    );
  };
  return (
    <div className="pharmacy-claims-wrapper">
      {claims?.length > 0 ? (
        <>
          {renderHeader()}
          {renderPharmacyClaimTable()}
          {ignoreOpenDialog && renderIgnoreDialog()}
        </>
      ) : (
        <div className="no-claim-record">
          {t('No claim records available for this patient')}
        </div>
      )}
    </div>
  );
};
export default PharmacyClaimsPatientDetailsDrawer;
